import { BandChart, SkeletonLoader } from '@toggle/design-system';
import { CalculatorResponse } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getHorizonToIndexMap } from '~/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { useThemePerformance } from '~/react-query-hooks/themes/useThemeOverview';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import { PriceReturnPill } from '~/views/theme-overview/theme-overview-drawer/components/theme-performance-section/price-return-pill/PriceReturnPill';
import {
  SimpleHorizon,
  SimpleHorizonSelector,
} from '~/views/theme-overview/theme-overview-drawer/components/theme-performance-section/simple-horizon-selector/SimpleHorizonSelector';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import styles from './ThemePerfBandChart.module.scss';
import * as S from './ThemePerfBandChart.styles';

interface ThemePerfBandChartProps {
  entityTags: string[];
  isFetching?: boolean;
  defaultHorizon?: SimpleHorizon;
  showHorizonSelector?: boolean;
  themeId: string;
}

export const ThemePerfBandChart = ({
  themeId,
  entityTags,
  isFetching,
  defaultHorizon = '1M',
  showHorizonSelector = true,
}: ThemePerfBandChartProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const [selectedHorizon, setSelectedHorizon] =
    useState<SimpleHorizon>(defaultHorizon);
  const horizonToIndexMap = getHorizonToIndexMap(1);

  const {
    data: themePerformanceData,
    isFetching: isThemePerformanceFetching,
    error: themePerformanceError,
  } = useThemePerformance(
    entityTags,
    horizonToIndexMap.get(selectedHorizon.toLowerCase())
  );

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: CalculatorResponse) =>
    data?.aggregate.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const handleHorizonChange = (horizon: SimpleHorizon) => {
    setSelectedHorizon(horizon);
  };

  const tsData = formatData(themePerformanceData);
  const xAxisCalendarStep = selectedHorizon === '1M' ? 'week' : 'month';

  if (themePerformanceError) {
    throw themePerformanceError;
  }

  return (
    <div className={styles.root}>
      <div className={styles.chartTitleWrapper}>
        {showHorizonSelector && (
          <SimpleHorizonSelector
            selectedHorizon={selectedHorizon}
            horizons={['1W', '1M', '3M', '6M', '12M']}
            handleHorizonChange={handleHorizonChange}
          />
        )}
      </div>
      <div className={styles.headerWrapper}>
        <div className={styles.textWrapper}>
          <p className={styles.chartTitleText}>
            {t('widget:themeOverview.themePerformance')}
          </p>
          <p className={styles.descriptionText}>
            {`${t('themeOverview.equalWeightedBasket')} ${t(
              'themeOverview.topCompanies',
              { total }
            )}`}
          </p>
        </div>
        <PriceReturnPill horizon={selectedHorizon} themeId={themeId} />
      </div>
      {isThemePerformanceFetching || !!isFetching ? (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '248px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      ) : (
        <div className={styles.chartAndHorizonsWrapper}>
          <S.BandChartWrapper $color={colors.default.value}>
            <BandChart
              tsData={tsData}
              showLine
              domainLimited={false}
              includeXScalePadding={false}
              isBordered
              fillColors={{
                positive: colors.threshold.positive.value,
                negative: colors.threshold.negative.value,
              }}
              isPercent
              locale={languageSetting}
              xAxisCalendarStep={xAxisCalendarStep}
            />
          </S.BandChartWrapper>
        </div>
      )}
    </div>
  );
};
